import { skipToken } from '@reduxjs/toolkit/query'
import { head, omit, pickBy, values } from 'lodash-es'
import { useRouter } from 'next/router'
import queryString from 'query-string'
import { useCallback, useMemo } from 'react'
import {
  createEnumParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params'
import { WorkspacesAPITagType } from '@/api/api'
import { useFetchUserStatusQuery } from '@/api/user-api'
import { useKolRadarSearchQuery } from '@/api/workspace-api/search-api'
import { PlatformParam } from '@/components/hashtag/common/hashtag-filter'
import { FOLLOWER_COUNT_RANGE_RECORD } from '@/components/kol/common/follower-level-selector'
import {
  ANY_KOL_TYPE_CODE,
  EXCLUDED_ALL_KOL_TYPE_CODE,
} from '@/components/search/filters/common/with-official-filter-content'
import { VerifiedKolParam } from '@/components/search/filters/kol/others-filter-group'
import useAvailableCountries from '@/components/search/hooks/use-available-countries'
import {
  AllCountryCodeParam,
  UNLIMITED_COUNTRY_CODE,
} from '@/constants/country-code'
import {
  defaultKolSortOption,
  kolOptionSchema,
  KolSortOption,
} from '@/constants/search/kol-sort-option'
import { SearchMode } from '@/constants/search/search-mode'
import { Page } from '@/hooks/use-authorization/constants'
import useIsDefaultSearch from '@/hooks/use-is-default-search'
import { useSelector } from '@/hooks/use-selector'
import useUserMetadata from '@/hooks/use-user-metadata'
import { useIntl } from '@/i18n/hooks/use-intl'
import {
  Condition,
  KolRadarSearchDefaultSearchKeys,
  KolRadarSearchRequestBasicProps,
  KolRadarSearchResponse,
} from '@/types/api/search'
import { Optional } from '@/types/optional'
import UseQueryResult from '@/types/rtk-query'
import { SortOption } from '@/types/search-types'
import { ampli, type SearchForKolNoResultProperties } from '@/utils/ampli'
import { PlatformShortcode } from '@/utils/convert-platform'
import { PotentialParam } from '@/utils/detail/potential'
import { getDefaultCountryCode } from '@/utils/get-default-country-code'
import isExternalOrigin from '@/utils/is-external-origin'
import removeNil from '@/utils/remove-nil'
import SearchIdManager from '@/utils/search-id-manager'
import { omitDefaultValue } from '@/utils/search/omit-default-value'
import { omitIfGreaterThanOrEqual } from '@/utils/search/omit-if-greater-than-or-equal'
import searchForKol from '@/utils/tracking/search-for-kol'
import { NoNullStringArrayParam } from '@/utils/use-query-params'

type UseKolSearch = () => {
  kolQueryParams: Condition & { sort?: KolSortOption }
  kolResult: UseQueryResult<KolRadarSearchResponse>
  mode: SearchMode
  sortOptions: SortOption<KolSortOption>[]
  isDefaultSearch: boolean
  handleSearchKol: (params: Condition) => void
}

const useKolSearch: UseKolSearch = () => {
  const router = useRouter()
  const { userMetadata } = useUserMetadata()
  const { getSortOptions } = useSortOptions()
  const viewModeForKol = useSelector((state) => state.search.viewModeForKol)
  const hasAnyKolRadarSearchCache = useSelector(
    (state) => !!state.api.queries.kolRadarSearch,
  )
  const hasKolRadarSearchRecord = useSelector(
    (state) =>
      state.api.provided.hasOwnProperty(WorkspacesAPITagType.KolRadarSearch), // 只要有 call 過 radar-search，不管還有沒有元件在訂閱，這個值都會存在
  )
  const isUserLandingSearchPage = useSelector(
    (state) => state.route.initialPathname === Page.Search,
  )

  const { data: userStatus } = useFetchUserStatusQuery()
  const { setIsDefaultSearch } = useIsDefaultSearch()
  const { checkIsDefaultSearch } = useCheckIsDefaultSearch()

  const defaultCountryCode = getDefaultCountryCode(
    userStatus?.currentWorkspace.countryCode,
    userStatus?.currentWorkspace.countryCode,
    userStatus?.currentWorkspace.availableServices,
    true,
  )

  const [uiQuery] = useQueryParams({
    mode: withDefault(SearchModeParams, SearchMode.Kol),
  })
  const mode = uiQuery.mode

  const [kolQuery] = useQueryParams(searchKolDefaultParams, {
    searchStringToObject: (searchString) =>
      queryString.parse(searchString, { arrayFormat: 'bracket' }),
  })

  const kolQueryParams = useMemo(() => {
    return {
      ...kolQuery,
      sort: ((): KolSortOption | undefined => {
        const sort = kolOptionSchema.safeParse(kolQuery.sort)
        return sort.success ? sort.data : undefined
      })(),
      country_code: kolQuery.country_code ?? defaultCountryCode,
      keywordFrom:
        kolQuery.keywordFrom as SearchForKolNoResultProperties['keywordFrom'],
    }
  }, [defaultCountryCode, kolQuery])

  const { isCountrySupported: hasAvailableCountryResult } =
    useAvailableCountries()

  const kolSearchParams = mapKolSearchParams(
    kolQueryParams,
    hasAnyKolRadarSearchCache,
  )

  const kolResult = useKolRadarSearchQuery(
    mode === SearchMode.Kol &&
      router.pathname === Page.Search &&
      hasAvailableCountryResult(kolQueryParams)
      ? {
          params: kolSearchParams,
          beforeQueryStarts: ({ args }): void => {
            const isDefault = checkIsDefaultSearch(
              args.params,
              defaultCountryCode,
            )
            setIsDefaultSearch(isDefault)
            // 看更多的話，不觸發 searchForKol 事件 ref: https://www.notion.so/ikala/Amp-searchId-2932a2c39cf143dd8a4e8a0ad51c4bab?pvs=4
            const hasAnchor = !!args.params.anchor
            if (!hasAnchor) {
              const searchId = SearchIdManager.getInstance().update()

              searchForKol(kolSearchParams, {
                searchId,
                view: viewModeForKol,
                isDefault,
                aiEnabled: !!userMetadata?.kol_ai_search_mode,
                isExternalLink:
                  isExternalOrigin() && // 防止從 kol-radar 內部頁面，開新頁面至 search 頁（後端 route case）
                  isUserLandingSearchPage && // 防止從 kol-radar 內部頁面，轉導至 search 頁（前端 route case）
                  !hasKolRadarSearchRecord, // 防止從在 search 頁搜尋很多次
              })
            }
          },
          onQueryFulfilled: ({ response, args }): void => {
            if (response.list.length > 0) {
              ampli.viewSearchResult({
                kolResult: response.list.map((kol) => kol.id),
                from: args.params.from ?? 'General Search',
                isDefault: checkIsDefaultSearch(
                  args.params,
                  defaultCountryCode,
                ),
              })
            }
          },
        }
      : skipToken,
  )

  const withAvailableSortOption = (
    keyword: Optional<string>,
    sort: Optional<string>,
  ): Optional<string> => {
    if (!sort) {
      return sort
    }

    const options: SortOption<KolSortOption>[] = getSortOptions(keyword)
    const defaultOption = head(options)
    const foundOption = options.find((option) => option.value === sort)

    if (foundOption) {
      return foundOption.disabled ? defaultOption?.value : foundOption.value
    }

    return defaultOption?.value
  }

  const withAvailablePlatformOption = (
    platform?: PlatformShortcode,
  ): PlatformShortcode | undefined => {
    if (!platform) {
      return undefined
    }

    if (mode === SearchMode.Kol) {
      if (
        ![
          PlatformShortcode.Instagram,
          PlatformShortcode.Facebook,
          PlatformShortcode.YouTube,
          PlatformShortcode.Twitter,
          PlatformShortcode.TikTok,
        ].includes(platform)
      ) {
        return undefined
      }
    }

    // 程式運行到這邊，代表是受支援的平台
    return platform
  }

  const handleSearchKol = (params: Condition): void => {
    // 只取 query string 的部分
    const queryStringPart = router.asPath.includes('?')
      ? router.asPath.split('?')[1]
      : ''

    const payload = {
      filter_kol_type: omitDefaultValue(
        kolQueryParams.filter_kol_type,
        ANY_KOL_TYPE_CODE,
      ),
      ...queryString.parse(queryStringPart, {
        arrayFormat: 'bracket',
      }),
      ...params,
    }
    const keyword = payload.keyword ?? undefined
    const query = removeNil({
      ...payload,
      keyword,
      anchor: params.anchor ?? undefined,
      mode: SearchMode.Kol,
      platform_type: withAvailablePlatformOption(payload.platform_type),
      sort: withAvailableSortOption(keyword, payload.sort),
    })

    router.push(
      queryString.stringifyUrl(
        {
          url: Page.Search,
          query,
        },
        {
          arrayFormat: 'bracket',
        },
      ),
      undefined,
      {
        scroll: !params.anchor,
      },
    )
  }

  return {
    kolQueryParams,
    kolResult,
    mode,
    handleSearchKol,
    sortOptions: getSortOptions(kolQueryParams.keyword),
    isDefaultSearch: checkIsDefaultSearch(kolQueryParams, defaultCountryCode),
  }
}

const SearchModeParams = createEnumParam(values(SearchMode))

const searchKolDefaultParams = {
  keyword: withDefault(StringParam, undefined),
  keywordFrom: withDefault(StringParam, undefined),
  platform_type: withDefault(PlatformParam, undefined),
  country_code: withDefault(AllCountryCodeParam, undefined),
  verifiedKol: withDefault(VerifiedKolParam, undefined),
  potential: withDefault(PotentialParam, undefined),
  customized_tags_names: withDefault(StringParam, undefined),
  gender: withDefault(StringParam, undefined),
  avgPvRateBegin: withDefault(NumberParam, undefined),
  avgPvRateEnd: withDefault(NumberParam, undefined),
  interactiveRateBegin: withDefault(NumberParam, undefined),
  interactiveRateEnd: withDefault(NumberParam, undefined),
  followerRangeLevel: withDefault(NumberParam, undefined),
  fansUpRateBegin: withDefault(NumberParam, undefined),
  fansUpRateEnd: withDefault(NumberParam, undefined),
  filter_collection: withDefault(StringParam, undefined),
  exclude_kol_collection_folder_ids: withDefault(StringParam, undefined),
  filter_cooperation: withDefault(StringParam, undefined),
  filter_unrecommended: withDefault(StringParam, undefined),
  filter_kol_type: withDefault(StringParam, EXCLUDED_ALL_KOL_TYPE_CODE),
  audienceGenderCode: withDefault(StringParam, undefined),
  audienceGenderRateBegin: withDefault(NumberParam, undefined),
  audienceGenderRateEnd: withDefault(NumberParam, undefined),
  audienceAgeCode: withDefault(StringParam, undefined),
  audienceAgeRateBegin: withDefault(NumberParam, undefined),
  audienceAgeRateEnd: withDefault(NumberParam, undefined),
  audienceGeoCode: withDefault(StringParam, undefined),
  audienceGeoRateBegin: withDefault(NumberParam, undefined),
  audienceGeoRateEnd: withDefault(NumberParam, undefined),
  minQuotationPrice: withDefault(NumberParam, undefined),
  maxQuotationPrice: withDefault(NumberParam, undefined),
  follower_start_from: withDefault(NumberParam, undefined),
  follower_end_to: withDefault(NumberParam, undefined),
  post_start_time: withDefault(StringParam, undefined),
  post_end_time: withDefault(StringParam, undefined),
  sort: withDefault(StringParam, defaultKolSortOption),
  languages: withDefault(NoNullStringArrayParam, undefined),
  anchor: withDefault(StringParam, undefined),
  from: withDefault(StringParam, undefined),
}

type UseSortOptionsProps = () => {
  getSortOptions: (keyword?: string) => SortOption<KolSortOption>[]
}

const useSortOptions: UseSortOptionsProps = () => {
  const { formatMessage } = useIntl()

  const getSortOptions = useCallback(
    (keyword?: string): SortOption<KolSortOption>[] => {
      return [
        {
          value: KolSortOption.Recommend,
          label: formatMessage({ id: 'search:sorting_option_kolradar' }),
          disabled: false,
          tag: 'recommend',
        },
        {
          value: KolSortOption.FollowerCount,
          label: formatMessage({ id: 'search:sorting_option_fans_descending' }),
          disabled: false,
          tag: 'follower',
        },
        {
          value: KolSortOption.MaxFansUpRate3Month,
          label: formatMessage({
            id: 'search:sorting_option_growth_descending',
          }),
          disabled: false,
          tag: 'fans-up-rate',
        },
        {
          value: KolSortOption.MaxInteractiveRate3Month,
          label: formatMessage({
            id: 'search:sorting_option_engagement_rate_descending',
          }),
          disabled: false,
          tag: 'interactive-rate',
        },
        {
          value: KolSortOption.MaxAvgPvRate3Month,
          label: formatMessage({ id: 'search:filter_view_rate_label' }),
          disabled: false,
          tag: 'pv-rate',
        },
        {
          value: KolSortOption.MatchedPost,
          label: formatMessage({
            id: 'search:sorting_option_keyword_descending',
          }),
          disabled: !keyword,
          tag: 'matched-post',
        },
      ]
    },
    [formatMessage],
  )

  return {
    getSortOptions,
  }
}

type UseCheckIsDefaultSearchProps = () => {
  checkIsDefaultSearch: (
    conditions: Pick<Condition, 'country_code' | 'sort' | 'filter_kol_type'>,
    defaultCountryCode: string,
  ) => boolean
}

const useCheckIsDefaultSearch: UseCheckIsDefaultSearchProps = () => {
  const isOnTourFlow = useSelector((state) => state.tour.tourModalVisible)

  const checkIsDefaultSearch = (
    conditions: KolRadarSearchRequestBasicProps,
    defaultCountryCode: string,
  ): boolean => {
    if (isOnTourFlow) {
      return true
    }

    const conditionsToCheck = omit(conditions, ['anchor', 'from'])
    const filterConditions = pickBy(conditionsToCheck, (value) => !!value)

    if (
      Object.keys(filterConditions).length ===
      KolRadarSearchDefaultSearchKeys.length
    ) {
      return (
        filterConditions.country_code === defaultCountryCode &&
        filterConditions.sort === defaultKolSortOption &&
        filterConditions.filter_kol_type === EXCLUDED_ALL_KOL_TYPE_CODE
      )
    }

    return Object.keys(filterConditions).length === 0
  }

  return {
    checkIsDefaultSearch,
  }
}

export const mapKolSearchParams = (
  kolQueryParams: Condition,
  hasAnyKolRadarSearchCache: boolean,
): KolRadarSearchRequestBasicProps => {
  return removeNil({
    ...kolQueryParams,
    // 過濾掉空字串
    keyword: !!kolQueryParams.keyword ? kolQueryParams.keyword : undefined,
    // 搜尋上限超過 1M+，就不帶入
    follower_end_to: omitIfGreaterThanOrEqual(
      kolQueryParams.follower_end_to,
      FOLLOWER_COUNT_RANGE_RECORD.level8.followerStartFrom,
    ),
    filter_kol_type: omitDefaultValue(
      kolQueryParams.filter_kol_type,
      ANY_KOL_TYPE_CODE,
    ),
    country_code: omitDefaultValue(
      kolQueryParams.country_code,
      UNLIMITED_COUNTRY_CODE,
    ),
    anchor: hasAnyKolRadarSearchCache ? kolQueryParams.anchor : undefined,
    languages: kolQueryParams.languages?.join('|'),
  })
}

export default useKolSearch
